import { resolveMemberPortalDisplayFirstName } from './memberDisplayName'

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim()
}

export const MEMBER_PROFILE_RESOLUTION_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  RESOLVED: 'resolved',
  FAILED: 'failed',
})

export function hasLinkedMemberProfile(memberProfileDetails) {
  if (!memberProfileDetails || typeof memberProfileDetails !== 'object') return false
  return Boolean(
    memberProfileDetails.id ||
      memberProfileDetails.member_id ||
      memberProfileDetails.memberId ||
      memberProfileDetails.first_name ||
      memberProfileDetails.firstName ||
      memberProfileDetails.preferred_name ||
      memberProfileDetails.preferredName ||
      memberProfileDetails.membership_number ||
      memberProfileDetails.member_number
  )
}

export function createAuth0MemberProfile({ user, clubCode } = {}) {
  if (!user) return null
  const fallbackName = user.email?.split?.('@')?.[0] || clubCode
  const firstName = resolveMemberPortalDisplayFirstName({
    auth0User: user,
    fallback: fallbackName,
  })
  const lastName = user.last_name || user.family_name || ''
  const memberId =
    user?.member_id ||
    user?.memberId ||
    user?.memberID ||
    user?.app_metadata?.member_id ||
    user?.['https://privana.club/member_id'] ||
    null
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || fallbackName
  return {
    id: memberId,
    firstName,
    first_name: firstName,
    lastName,
    last_name: lastName,
    full_name: fullName,
    email: user.email,
  }
}

export function createResolvingMemberDisplayProfile() {
  return {
    isResolving: true,
    firstName: '',
    first_name: '',
    full_name: '',
    email: '',
    avatarUrl: null,
  }
}

export function resolveMemberDisplayProfile({
  memberProfile,
  memberProfileDetails,
  memberProfileResolutionStatus,
  user,
  clubCode,
} = {}) {
  const status = memberProfileResolutionStatus || MEMBER_PROFILE_RESOLUTION_STATUS.IDLE
  const hasLinkedMember = hasLinkedMemberProfile(memberProfileDetails)
  const shouldWaitForResolution =
    Boolean(user && clubCode) &&
    !hasLinkedMember &&
    (status === MEMBER_PROFILE_RESOLUTION_STATUS.IDLE || status === MEMBER_PROFILE_RESOLUTION_STATUS.LOADING)

  if (shouldWaitForResolution) {
    return createResolvingMemberDisplayProfile()
  }

  const baseProfile = hasLinkedMember ? memberProfileDetails : memberProfile

  if (!baseProfile) return null

  const allowAuth0Fallback = !hasLinkedMember && (
    status === MEMBER_PROFILE_RESOLUTION_STATUS.RESOLVED ||
      status === MEMBER_PROFILE_RESOLUTION_STATUS.FAILED
  )

  const firstName = resolveMemberPortalDisplayFirstName({
    member: baseProfile,
    auth0User: allowAuth0Fallback ? user : null,
    fallback: allowAuth0Fallback ? memberProfile?.first_name || memberProfile?.firstName || memberProfile?.email : 'Member',
  })
  const lastName =
    memberProfileDetails?.last_name ||
    memberProfileDetails?.lastName ||
    memberProfile?.last_name ||
    memberProfile?.lastName ||
    ''
  const fullName = [firstName, lastName].filter(Boolean).join(' ')
  const avatarUrl =
    memberProfileDetails?.profile_picture ||
    memberProfileDetails?.profile_picture_url ||
    memberProfile?.profile_picture ||
    memberProfile?.profile_picture_url ||
    null
  const preferredName =
    normalizeString(memberProfileDetails?.preferred_name ?? memberProfileDetails?.preferredName) ||
    normalizeString(memberProfile?.preferred_name ?? memberProfile?.preferredName)

  return {
    ...memberProfile,
    ...(hasLinkedMember ? memberProfileDetails : null),
    isResolving: false,
    id: hasLinkedMember ? memberProfileDetails?.id || memberProfile?.id : memberProfile?.id,
    member_id: hasLinkedMember
      ? memberProfileDetails?.member_id || memberProfileDetails?.memberId || memberProfile?.member_id || memberProfile?.id
      : memberProfile?.member_id || memberProfile?.id,
    first_name: firstName,
    firstName,
    preferred_name: preferredName || undefined,
    last_name: lastName,
    lastName,
    full_name: fullName || memberProfile?.full_name,
    membership_number:
      (hasLinkedMember && (memberProfileDetails?.membership_number || memberProfileDetails?.member_number)) ||
      memberProfile?.membership_number ||
      memberProfile?.member_number ||
      memberProfile?.id,
    avatarUrl,
  }
}
